import {
    onKontrolKancasiniAyarla,
    SenkronOnKontrolZamanlayici
} from "@/plugins/uyap-plugin/SenkronOnKontrolZamanlayici";
import {
    sabahRaporuKancasiniAyarla,
    SenkronSabahRaporuZamanlayici
} from "@/plugins/uyap-plugin/SenkronSabahRaporuZamanlayici";
import {senkronOnKontroluCalistir} from "@/plugins/senkronOnKontrol";
import {senkronSabahRaporunuCalistir} from "@/plugins/senkronSabahRaporu";
import {senkronAcikMi} from "@/utils/senkronKapisi";

/**
 * Faz 3 zamanlayıcılarının uygulamaya bağlanması (DEV-2967 + DEV-2968).
 *
 * `App.vue` `created()`ten bir kez çağrılır; `BaglantiKurtarma` kancalarının
 * kaydedildiği yerle aynı desen. Görev Yöneticisi'nin `taskInterval`ıyla hiçbir
 * ilişkisi yoktur ve o dosyaya dokunulmamıştır.
 *
 * OTOMATİK SORGU KAPISI (DEV-2603, karar 20.10.2026): iki zamanlayıcı yalnız
 * store'daki `senkronAktif` getter'ı AÇIKKEN kurulur. Kapıyı kurulum anında
 * okumak yetmez: `App.created` sırasında kullanıcı (`/ben`) henüz yüklenmemiş
 * olabilir ve kapı sonradan açılıp kapanabilir. Bu yüzden getter İZLENİR:
 * açılınca kurulur, kapanınca durdurulur. Kapalıyken zamanlayıcı hiç kurulmadığı
 * için günlük ateşleme damgası da harcanmaz; kapı açıldığı gün saat geçmiş olsa
 * bile yakalama o gün koşar. Ateşleme anında kapı ayrıca iş fonksiyonlarında
 * (`senkronOnKontroluCalistir` / `senkronSabahRaporunuCalistir`) yeniden okunur ve
 * açıksa sunucudan teyit edilir (`senkronAcikMiTazeleyerek`).
 */

/** Kapı izleyicisini bırakan fonksiyon (tekrar bağlamada/durdururken sızıntı olmasın). */
let kapiIzleyicisiniBirak: (() => void) | null = null;

/** "22:45" -> [22, 45]. Bozuk değerde null (zamanlayıcı hiç kurulmaz). */
export function saatiParcala(saat: string): [number, number] | null {
    const parcalar = String(saat ?? "").split(":");
    if (parcalar.length < 2) return null;

    const s = Number(parcalar[0]);
    const d = Number(parcalar[1]);
    if (!Number.isInteger(s) || !Number.isInteger(d)) return null;
    if (s < 0 || s > 23 || d < 0 || d > 59) return null;

    return [s, d];
}

function baslat(store: any): void {
    // Kapalıyken HİÇBİR zamanlayıcı kurulmaz (ve gün damgası yazılmaz).
    if (!senkronAcikMi(store)) return;

    const onKontrol = saatiParcala(store.getters["senkronAyarlari/onKontrolSaati"]);
    if (onKontrol) SenkronOnKontrolZamanlayici.baslat(onKontrol[0], onKontrol[1]);

    const sabah = saatiParcala(store.getters["senkronAyarlari/sabahRaporuSaati"]);
    if (sabah) SenkronSabahRaporuZamanlayici.baslat(sabah[0], sabah[1]);
}

function durdur(): void {
    SenkronOnKontrolZamanlayici.durdur();
    SenkronSabahRaporuZamanlayici.durdur();
}

/**
 * Kapı getter'ını izler: açılınca zamanlayıcıları kurar (yakalama dahil), kapanınca
 * durdurur. `watch` desteklemeyen yalın store'larda (yalnız getter taşıyan sahte
 * nesneler) izleme atlanır; kurulum yine kapının o anki değerine bakar.
 */
function kapiyiIzle(store: any): void {
    if (kapiIzleyicisiniBirak) {
        kapiIzleyicisiniBirak();
        kapiIzleyicisiniBirak = null;
    }
    if (!store || typeof store.watch !== "function") return;

    kapiIzleyicisiniBirak = store.watch(
        (_state: any, getters: any) => getters.senkronAktif === true,
        (acik: boolean) => {
            if (acik) {
                // Önce durdur: çalışan bir handle varken `baslat` sessizce no-op döner.
                durdur();
                baslat(store);
            } else {
                durdur();
            }
        }
    );
}

/** Kancaları kaydeder, kapıyı izler ve (kapı açıksa) iki zamanlayıcıyı başlatır. */
export function senkronZamanlayicilariniBagla(store: any): void {
    store.commit("senkronAyarlari/INIT");

    // Store KANCAYA GEÇİRİLİR, modüller `app.$store` okumaz: `@/main`den okunan
    // app singleton'ı uygulama açılışında (dairesel import) bir süre `undefined`
    // olabiliyor ve bu sessiz hatalara yol açıyordu.
    onKontrolKancasiniAyarla(() => senkronOnKontroluCalistir(store));
    sabahRaporuKancasiniAyarla(() => senkronSabahRaporunuCalistir(store));

    kapiyiIzle(store);
    baslat(store);
}

/**
 * Kapı izleyicisini bırakır ve iki zamanlayıcıyı durdurur (kancalar kayıtlı kalır).
 * Uygulama kapanışı ve testler içindir.
 */
export function senkronZamanlayicilariniDurdur(): void {
    if (kapiIzleyicisiniBirak) {
        kapiIzleyicisiniBirak();
        kapiIzleyicisiniBirak = null;
    }
    durdur();
}

/**
 * Saat ayarı değişince yeniden kurar.
 *
 * `baslat()` kendi başına yeni saati alamaz: çift başlatma koruması yüzünden
 * ikinci `baslat` çağrısı sessizce döner ve zamanlayıcı ESKİ saatle koşmaya
 * devam ederdi. Bu yüzden önce `durdur()`.
 */
export function senkronZamanlayicilariniYenidenKur(store: any): void {
    durdur();
    baslat(store);
}
